<template>
  <div>
    <CCard>
      <CCardHeader>
        <strong>Router-ID 分配</strong>
        <small class="text-muted ml-2">从网段抽取 /32 分配给路由器（OSPF/BGP router-id 用）</small>
        <div class="float-right">
          <CButtonGroup>
            <CButton color="primary" size="sm" @click="openPoolModal">添加网段池</CButton>
            <CButton color="secondary" size="sm" :disabled="loading" @click="loadList">刷新</CButton>
          </CButtonGroup>
        </div>
      </CCardHeader>
      <CCardBody>
        <div v-for="p in pools" :key="p.id" class="mb-3">
          <div class="pool-header">
            <strong>{{ p.network }}</strong>
            <CBadge v-if="regionName(p.region_id)" color="dark" class="ml-2">{{ regionName(p.region_id) }}</CBadge>
            <CBadge color="info" class="ml-2">已分配 {{ p.used }} / {{ p.total }}</CBadge>
            <small v-if="p.remark" class="text-muted ml-2">{{ p.remark }}</small>
            <span class="ml-auto">
              <CButtonGroup size="sm">
                <CButton color="success" size="sm" @click="openAllocModal(p)">分配</CButton>
                <CButton color="danger" size="sm" :disabled="p.used > 0" @click="deletePool(p)">删除池</CButton>
              </CButtonGroup>
            </span>
          </div>
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th width="180">IP (/32)</th>
                <th>路由器</th>
                <th>备注</th>
                <th>分配时间</th>
                <th width="80">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in p.allocations" :key="a.id">
                <td><code>{{ a.ip }}</code></td>
                <td>{{ a.router_name || ('#' + a.router_id) }}</td>
                <td>{{ a.remark || '' }}</td>
                <td><small>{{ a.created_at }}</small></td>
                <td>
                  <CButton color="danger" size="sm" @click="releaseAllocation(a, p)">释放</CButton>
                </td>
              </tr>
              <tr v-if="p.allocations.length === 0">
                <td colspan="5" class="text-center text-muted">暂无分配</td>
              </tr>
            </tbody>
          </table>
        </div>
        <CAlert v-if="!loading && pools.length === 0" color="info" fade>
          暂无网段池。点击右上角「添加网段池」创建（如 198.18.255.0/24）。
        </CAlert>
      </CCardBody>
    </CCard>

    <!-- 添加池 Modal -->
    <CModal :show.sync="poolModal.show" title="添加 Router-ID 网段池" color="primary">
      <CForm>
        <CInput label="网段（CIDR）" v-model="poolModal.network" placeholder="如 198.18.255.0/24" />
        <CSelect label="地域（可选）" v-model="poolModal.region_id" :options="regionSelectOptions" placeholder="（全局）" />
        <CInput label="备注" v-model="poolModal.remark" placeholder="可选" />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="poolModal.show = false">取消</CButton>
          <CButton color="success" :disabled="poolModal.loading" @click="submitPool">{{ poolModal.loading ? '创建中...' : '创建' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 分配 Modal -->
    <CModal :show.sync="allocModal.show" :title="'分配 Router-ID - ' + (allocModal.pool ? allocModal.pool.network : '')" color="success">
      <CForm>
        <div class="form-group">
          <label class="form-label">路由器</label>
          <SearchSelect :options="routerSelectOptions" v-model="allocModal.router_id" placeholder="输入路由器名筛选" />
        </div>
        <CInput label="IP（留空 = 自动分配空闲 /32）" v-model="allocModal.ip" placeholder="如 198.18.255.1" />
        <CInput label="备注" v-model="allocModal.remark" placeholder="可选" />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="allocModal.show = false">取消</CButton>
          <CButton color="success" :disabled="allocModal.loading" @click="submitAlloc">{{ allocModal.loading ? '分配中...' : '分配' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 通用确认 Modal -->
    <CModal :show.sync="confirmModal.show" :title="confirmModal.title" color="danger">
      <p>{{ confirmModal.message }}</p>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="confirmModal.show = false">取消</CButton>
          <CButton color="danger" @click="confirmModal.callback(); confirmModal.show = false">确认</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
import SearchSelect from '@/components/SearchSelect'

export default {
  name: 'RouterIdList',
  components: { SearchSelect },
  data() {
    return {
      loading: false,
      pools: [],
      regions: [],
      allRouters: [],
      poolModal: {
        show: false,
        loading: false,
        network: '',
        region_id: '',
        remark: ''
      },
      allocModal: {
        show: false,
        loading: false,
        pool: null,
        router_id: '',
        ip: '',
        remark: ''
      },
      confirmModal: {
        show: false, title: '确认', message: '', callback: () => {}
      }
    }
  },
  computed: {
    regionSelectOptions() {
      return this.regions
        .filter(g => g.id !== 0)
        .map(g => ({ value: g.id, label: `${g.name} (${g.region_id})` }))
    },
    routerSelectOptions() {
      return this.allRouters.map(r => ({ value: r.id, label: r.name }))
    }
  },
  mounted() {
    this.loadList()
    this.loadRegions()
    this.loadRouters()
  },
  methods: {
    regionName(regionId) {
      if (!regionId) return null
      const g = this.regions.find(x => x.id === regionId)
      return g ? g.name : null
    },
    async loadRegions() {
      try {
        const res = await this.$axios.get('user/region/list')
        if (res.data.code === 200) {
          this.regions = res.data.data || []
        }
      } catch (e) { /* ignore */ }
    },
    async loadRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.allRouters = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/router-id/list')
        if (res.data.code === 200) {
          this.pools = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    showConfirm(title, message, callback) {
      this.confirmModal.title = title
      this.confirmModal.message = message
      this.confirmModal.callback = callback
      this.confirmModal.show = true
    },
    openPoolModal() {
      this.poolModal.network = ''
      this.poolModal.region_id = ''
      this.poolModal.remark = ''
      this.poolModal.show = true
    },
    async submitPool() {
      if (!this.poolModal.network) {
        this.$toasted.show('请填写网段', { type: 'error' })
        return
      }
      this.poolModal.loading = true
      try {
        const res = await this.$axios.post('user/router-id/pool/add', {
          network: this.poolModal.network,
          region_id: this.poolModal.region_id,
          remark: this.poolModal.remark
        })
        if (res.data.code === 200) {
          this.$toasted.show('已创建', { type: 'success' })
          this.poolModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '创建失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.poolModal.loading = false
    },
    deletePool(p) {
      this.showConfirm('确认删除', `确认删除网段池 ${p.network}？`, async () => {
        try {
          const res = await this.$axios.delete(`user/router-id/pool/${p.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已删除', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    },
    openAllocModal(p) {
      this.allocModal.pool = p
      this.allocModal.router_id = ''
      this.allocModal.ip = ''
      this.allocModal.remark = ''
      this.allocModal.show = true
    },
    async submitAlloc() {
      if (!this.allocModal.router_id) {
        this.$toasted.show('请选择路由器', { type: 'error' })
        return
      }
      this.allocModal.loading = true
      try {
        const res = await this.$axios.post('user/router-id/allocate', {
          router_id: this.allocModal.router_id,
          pool_id: this.allocModal.pool.id,
          ip: this.allocModal.ip,
          remark: this.allocModal.remark
        })
        if (res.data.code === 200) {
          this.$toasted.show(`已分配 ${res.data.data.ip} → ${res.data.data.router}`, { type: 'success' })
          this.allocModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '分配失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.allocModal.loading = false
    },
    releaseAllocation(a, p) {
      this.showConfirm('确认释放', `确认释放 ${a.ip}（${a.router_name || '#' + a.router_id}）？`, async () => {
        try {
          const res = await this.$axios.delete(`user/router-id/allocation/${a.id}/delete`)
          if (res.data.code === 200) {
            this.$toasted.show('已释放', { type: 'success' })
            this.loadList()
          } else {
            this.$toasted.show(res.data.msg || '释放失败', { type: 'error' })
          }
        } catch (e) {
          this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
        }
      })
    }
  }
}
</script>

<style scoped>
.pool-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #e9ecef;
  border-radius: 4px;
  margin-bottom: 8px;
}
.ml-auto {
  margin-left: auto;
}
</style>
